import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Building2, Trash2, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Combobox } from './ui/combobox';
import { CommanServices } from '@/services/commanServices';

// Create a new department directly from the incident page — mirrors the
// Departments & Units module: the record lands in db.departments (the single
// reference collection), members are stored as {id, reporting_enabled,
// is_leader} exactly like the module's Manage Members panel, and the new
// department is auto-linked (bidirectionally) to the incident's location with
// the chosen primary/secondary relationship, same as the Locations & Zones
// module link flow.
const CreateDepartmentDialog = ({ incident = null, locationId, locationName = '', onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('primary');
  const [saving, setSaving] = useState(false);

  // Members picker — same source + shape as the module's Manage Members panel
  const [allUsers, setAllUsers] = useState([]);
  const [members, setMembers] = useState([]); // [{id, name, email, is_leader}]
  const [comboValue, setComboValue] = useState('');

  useEffect(() => {
    CommanServices.getAppUsers()
      .then((res) => { if (res.status) setAllUsers(res.data || []); })
      .catch(console.error);
  }, []);

  const memberIds = useMemo(() => new Set(members.map((m) => String(m.id))), [members]);

  const addMemberOptions = useMemo(() => (
    allUsers
      .filter((u) => u?.id && !memberIds.has(String(u.id)))
      .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, { sensitivity: 'base' }))
      .map((u) => {
        const namePart = (u.name || '').trim() || (u.user_account || '').trim() || '';
        const emailPart = (u.email || '').trim() || '';
        let label;
        if (namePart && emailPart) label = `${namePart} — ${emailPart}`;
        else if (emailPart) label = emailPart;
        else label = namePart || 'Unknown';
        return { value: u.id, label };
      })
  ), [allUsers, memberIds]);

  const addMember = (userId) => {
    const user = allUsers.find((u) => String(u.id) === String(userId));
    setComboValue('');
    if (!user) return;
    setMembers((prev) => [...prev, {
      id: String(user.id),
      name: user.name || user.email || 'Unknown',
      email: user.email || '',
      phone: user.phone || '',
      is_leader: false,
    }]);
  };

  const removeMember = (id) => setMembers((prev) => prev.filter((m) => m.id !== id));

  const toggleLeader = (id, isLeader) =>
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, is_leader: isLeader } : m)));

  const audit = async (detail) => {
    if (!incident?.id) return;
    try {
      await CommanServices.addIncidentAuditLog(incident.id, { action: 'Created', detail });
    } catch (e) { /* audit is best-effort */ }
  };

  const save = async () => {
    if (saving) return;
    const deptName = name.trim();
    if (!deptName) { toast.error('Department name is required'); return; }
    if (!locationId || locationId === 'unknown') { toast.error('The incident needs a mapped location first'); return; }
    setSaving(true);
    try {
      // 1) Create the department in the Departments & Units reference
      //    collection — members ride along in the module's exact shape, and
      //    associated_locations triggers the backend's bidirectional link
      //    with the incident location.
      const res = await CommanServices.adddepartments({
        department_name: deptName,
        description: incident?.id ? `Created from incident ${incident.id} during active incident` : '',
        associated_locations: [locationId],
        // Full member objects — the backend re-validates members against the
        // Department model, whose name/position/contact reject nulls.
        members: members.map((m) => ({
          id: m.id,
          name: m.name || 'Unknown',
          position: m.is_leader ? 'Leader' : 'Member',
          contact: m.email || m.phone || '',
          reporting_enabled: true,
          notification_enabled: true,
          is_leader: !!m.is_leader,
        })),
      });
      if (!res?.status || !res?.data?.id) {
        toast.error(res?.message || 'Failed to create department');
        return;
      }
      const dept = res.data;

      // 2) The create link defaults to secondary — promote when primary chosen
      //    (authoritative relationship endpoint, same one the modules use).
      if (relationship === 'primary') {
        const rel = await CommanServices.updatelocationsdepartmentsbyId(locationId, dept.id, { relationship_type: 'primary' });
        if (rel?.status === false) {
          toast.warning('Department created, but setting the primary relationship failed — adjust it in Locations & Zones');
        }
      }

      const leaderCount = members.filter((m) => m.is_leader).length;
      toast.success(`Department "${dept.department_name}" created and linked to ${locationName || 'the incident location'} (${relationship})`);
      await audit(
        `Created department '${dept.department_name}' with ${members.length} member(s)${leaderCount ? ` (${leaderCount} leader${leaderCount === 1 ? '' : 's'})` : ''} and linked it to location '${locationName || locationId}' as ${relationship} — from the incident`
      );
      onCreated && (await onCreated());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" data-testid="create-department-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-[480px] max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-indigo-600" />
            New Department
          </h3>
          <button onClick={onClose} data-testid="create-department-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        {/* Auto-link banner */}
        <div className="flex items-start gap-2 p-2.5 rounded-lg border bg-indigo-50 border-indigo-200 text-[11px] text-indigo-700 leading-relaxed">
          <Link2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            Saved to the <span className="font-bold">Departments & Units</span> module and auto-linked to the incident location{' '}
            <span className="font-bold">{locationName || locationId}</span> — the link also appears in Locations & Zones.
          </span>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-800">Department name <span className="text-red-500">*</span></label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Facility Security"
            data-testid="create-department-name"
          />
        </div>

        {/* Primary / secondary relationship with the incident location */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-800">Relationship to this location</label>
          <div className="flex gap-2">
            {['primary', 'secondary'].map((rel) => (
              <button
                key={rel}
                type="button"
                onClick={() => setRelationship(rel)}
                data-testid={`create-department-rel-${rel}`}
                className={`flex-1 py-2 rounded-lg border text-xs font-bold capitalize transition-colors ${
                  relationship === rel
                    ? rel === 'primary'
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-gray-700 text-white border-gray-700'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {rel}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400">Primary departments are the location&apos;s main responders; secondary ones support.</p>
        </div>

        {/* Members — same pattern as the module's Manage Members panel */}
        <div className="space-y-2 border-t pt-3">
          <label className="block text-sm font-medium text-gray-800">Add member</label>
          <Combobox
            value={comboValue}
            onValueChange={(userId) => { if (userId) addMember(userId); else setComboValue(''); }}
            placeholder="Type or select user to add…"
            options={addMemberOptions}
            emptyLabel="No users match"
            is_create={false}
            disabled={saving}
            className="border-gray-300"
          />

          {members.length > 0 && (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {members.map((m) => (
                <div key={m.id} className="border rounded-lg px-3 py-2 bg-gray-50 flex items-center justify-between gap-2" data-testid={`create-department-member-${m.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{m.name}</div>
                    {m.email && <div className="text-xs text-gray-500 truncate">{m.email}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-gray-600">Leader</span>
                      <Switch
                        checked={!!m.is_leader}
                        onCheckedChange={(checked) => toggleLeader(m.id, checked)}
                        disabled={saving}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMember(m.id)}
                      className="text-red-600 hover:text-red-700 h-7 px-2"
                      disabled={saving}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={save}
            disabled={saving || !name.trim()}
            data-testid="create-department-submit"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {saving ? 'Creating…' : 'Create Department'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateDepartmentDialog;
